import { api } from './api';

// Types matching the backend DTOs
export interface CourseDetail {
  courseId: number;
  courseName: string;
  courseDescription: string;
  subject: string;
  difficulty: string;
  overallProgress: number;
  averageScore: number | null;
  totalStudyTime: number; // in minutes
  enrolledAt: string;
  lastAccessed: string;
  enrollmentStatus: string;
  currentTopic: string | null;
  currentTopicId: number | null;
  topics: CourseTopic[];
  recentActivities: CourseActivity[];
  statistics: CourseStatistics;
  deliveryType?: string; // Course delivery type: SELF_TUTORED, SCHOOL_TEACHER, PRIVATE_TUTOR
  instructor?: string | null; // Instructor name (null for self-registered courses)
}

export interface CourseTopic {
  topicId: number;
  topicTitle: string;
  topicDescription: string | null;
  status: 'not_started' | 'in_progress' | 'completed';
  progress: number;
  startedAt: string | null;
  completedAt: string | null;
  estimatedTimeMinutes: number | null;
  quizScore: number | null;
  hasQuiz: boolean;
  hasAssignment: boolean;
  assignmentSubmitted: boolean;
}

export interface CourseActivity {
  id: number;
  type: 'topic_started' | 'topic_completed' | 'quiz_attempted' | 'quiz_completed' | 'assignment_submitted' | 'assignment_graded';
  title: string;
  description: string;
  timestamp: string;
  score: number | null;
  topicTitle: string;
  topicId: number;
  status: 'success' | 'warning' | 'error' | 'info';
}

export interface CourseStatistics {
  totalTopics: number;
  completedTopics: number;
  inProgressTopics: number;
  notStartedTopics: number;
  totalQuizzes: number;
  completedQuizzes: number;
  totalAssignments: number;
  submittedAssignments: number;
  gradedAssignments: number;
  averageQuizScore: number | null;
  averageAssignmentScore: number | null;
  totalStudySessions: number;
  totalStudyTimeMinutes: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  timestamp: string;
  errorCode?: string;
}

class CourseDetailService {
  /**
   * Get course detail for a child
   */
  async getCourseDetail(childId: number, courseId: number): Promise<CourseDetail> {
    try {
      const response = await api.get<ApiResponse<CourseDetail>>(
        `/api/children/${childId}/courses/${courseId}/details`
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch course detail');
      }
    } catch (error: any) {
      console.error('Error fetching course detail:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch course detail'
      );
    }
  }
}

export const courseDetailService = new CourseDetailService();

